import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { getTheme } from '../theme/theme';
import type { ColorMode } from '../types/content';

type ColorModeContextValue = {
  mode: ColorMode;
  toggleColorMode: () => void;
};

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

type ColorModeProviderProps = {
  children: ReactNode;
};

export function ColorModeProvider({ children }: ColorModeProviderProps): JSX.Element {
  const [mode, setMode] = useState<ColorMode>('dark');

  const toggleColorMode = (): void => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const theme = useMemo(() => getTheme(mode), [mode]);
  const value = useMemo<ColorModeContextValue>(() => ({ mode, toggleColorMode }), [mode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): ColorModeContextValue {
  const context = useContext(ColorModeContext);

  if (!context) {
    throw new Error('useColorMode must be used inside ColorModeProvider.');
  }

  return context;
}
